import { FileType } from '@nimbus-global/features/manage/model/file-type-policy.enum';
import { FileAttachmentIcons } from '@nimbus-library/features/shared/resources/file-attachment-icons';

const FILE_TYPE_ICON_MAP: Partial<Record<FileType, string>> = {
  [FileType.Pdf]: FileAttachmentIcons.Pdf,
  [FileType.Doc]: FileAttachmentIcons.MsWord,
  [FileType.Docx]: FileAttachmentIcons.MsWord,
  [FileType.Xls]: FileAttachmentIcons.MsExcel,
  [FileType.Xlsx]: FileAttachmentIcons.MsExcel,
  [FileType.Ppt]: FileAttachmentIcons.MsPowerPoint,
  [FileType.Pptx]: FileAttachmentIcons.MsPowerPoint,
  [FileType.Csv]: FileAttachmentIcons.Text,
  [FileType.Rtf]: FileAttachmentIcons.Text,
  [FileType.Tiff]: FileAttachmentIcons.Image,
  [FileType.Heic]: FileAttachmentIcons.Image,
  [FileType.Heif]: FileAttachmentIcons.Image,
  [FileType.Mov]: FileAttachmentIcons.Video,
  [FileType.Mpeg]: FileAttachmentIcons.Video,
  [FileType.Video3gp]: FileAttachmentIcons.Video,
  [FileType.Video3g2]: FileAttachmentIcons.Video,
  [FileType.Ac3]: FileAttachmentIcons.Audio,
  [FileType.Amr]: FileAttachmentIcons.Audio,
  [FileType.RealAudio]: FileAttachmentIcons.Audio,
  [FileType.Audio3gp]: FileAttachmentIcons.Audio,
  [FileType.Audio3g2]: FileAttachmentIcons.Audio,
};

const EXTENSION_FILE_TYPE_MAP: Record<string, FileType> = {
  pdf: FileType.Pdf,
  doc: FileType.Doc,
  docx: FileType.Docx,
  xls: FileType.Xls,
  xlsx: FileType.Xlsx,
  ppt: FileType.Ppt,
  pptx: FileType.Pptx,
  csv: FileType.Csv,
  rtf: FileType.Rtf,
  vcf: FileType.Vcf,
  ics: FileType.Ics,
  tiff: FileType.Tiff,
  tif: FileType.Tiff,
  heic: FileType.Heic,
  heif: FileType.Heif,
  mov: FileType.Mov,
  mpeg: FileType.Mpeg,
  mpg: FileType.Mpeg,
  ac3: FileType.Ac3,
  amr: FileType.Amr,
  ra: FileType.RealAudio,
};

const MIME_TYPE_FILE_TYPE_MAP: Record<string, FileType> = {
  'application/pdf': FileType.Pdf,
  'application/msword': FileType.Doc,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': FileType.Docx,
  'application/vnd.ms-excel': FileType.Xls,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': FileType.Xlsx,
  'application/vnd.ms-powerpoint': FileType.Ppt,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': FileType.Pptx,
  'text/csv': FileType.Csv,
  'text/rtf': FileType.Rtf,
  'application/rtf': FileType.Rtf,
  'text/vcard': FileType.Vcf,
  'text/calendar': FileType.Ics,
  'image/tiff': FileType.Tiff,
  'image/heic': FileType.Heic,
  'image/heic-sequence': FileType.Heic,
  'image/heif': FileType.Heif,
  'image/heif-sequence': FileType.Heif,
  'video/quicktime': FileType.Mov,
  'video/mpeg': FileType.Mpeg,
  'video/3gpp': FileType.Video3gp,
  'video/3gpp2': FileType.Video3g2,
  'audio/ac3': FileType.Ac3,
  'audio/amr': FileType.Amr,
  'audio/amr-nb': FileType.Amr,
  'audio/vnd.rn-realaudio': FileType.RealAudio,
  'audio/x-pn-realaudio': FileType.RealAudio,
  'audio/3gpp': FileType.Audio3gp,
  'audio/3gpp2': FileType.Audio3g2,
};

function getExtension(filename: string | null): string {
  const match = /\.([a-zA-Z0-9]+)$/.exec(filename ?? '');
  return match ? match[1].toLowerCase() : '';
}

/**
 * Resolves the allow-listed `FileType` (see file-type-policy.enum.ts) a chat attachment belongs
 * to, so its icon reflects what the admin's file-type policy actually governs rather than an
 * unrelated taxonomy (email attachment types cover a different, broader set of extensions).
 */
function resolveChatFileType(contentType: string | null, filename: string | null): FileType | null {
  const extension = getExtension(filename);
  if (extension && EXTENSION_FILE_TYPE_MAP[extension]) {
    return EXTENSION_FILE_TYPE_MAP[extension];
  }
  return MIME_TYPE_FILE_TYPE_MAP[(contentType ?? '').toLowerCase()] ?? null;
}

export function getChatAttachmentIcon(contentType: string | null, filename: string | null, themeFolder: 'light' | 'dark'): string {
  const fileType = resolveChatFileType(contentType, filename);
  const icon = (fileType && FILE_TYPE_ICON_MAP[fileType]) || FileAttachmentIcons.Default;
  return `images/file-attachment/${themeFolder}/${icon}`;
}
